/**
 * Duelo de Equipos — TABLERO en vivo para TV / Espectador (solo lectura).
 * /juego/duelo-equipos/:code/tv          → pantalla grande
 * /juego/duelo-equipos/:code/espectador  → mismo tablero, solo observa
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Clock, Trophy } from "lucide-react";

import { useLang } from "@/context/LangContext";
import { CanalesLogo } from "@/components/CanalesLogo";
import {
  wsUrl, TEAM_COLOR, PlayerCard, Options, useCountdown, DuelShell,
} from "@/components/duel/DuelBits";
import { playReveal, playMiss, playApplause } from "@/utils/sounds";

export default function TeamDuelBoard() {
  const { code } = useParams();
  const { lang } = useLang();
  const wsRef = useRef(null);
  const [state, setState] = useState(null);
  const [conn, setConn] = useState("connecting");
  const [reveal, setReveal] = useState(null);

  useEffect(() => {
    let ws, alive = true;
    try { ws = new WebSocket(wsUrl(code)); } catch { setConn("error"); return; }
    wsRef.current = ws;
    ws.onopen = () => { if (!alive) return; setConn("open"); ws.send(JSON.stringify({ type: "join", role: "tv" })); };
    ws.onclose = () => alive && setConn("closed");
    ws.onerror = () => alive && setConn("error");
    ws.onmessage = (ev) => {
      let msg; try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === "error") return;
      if (msg.status) setState(msg);
      switch (msg.type) {
        case "question": case "steal_open": case "sd_question": setReveal(null); break;
        case "correct": setReveal({ correct: msg.correct, selected: msg.selected }); playReveal(); break;
        case "steal_result": case "sd_result":
          setReveal({ correct: msg.correct, selected: msg.selected });
          msg.success ? playReveal() : playMiss(); break;
        case "wrong": setReveal({ correct: msg.correct, selected: msg.selected }); playMiss(); break;
        case "game_end": setReveal(null); playApplause(); break;
        default: break;
      }
    };
    return () => { alive = false; try { ws && ws.close(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const secs = useCountdown(state);

  if (conn === "connecting" || !state) {
    return <DuelShell><div className="text-center text-white/70 py-40"><Loader2 className="w-10 h-10 animate-spin inline" /><div className="mt-3">{lang === "es" ? "Conectando al tablero…" : "Connecting…"}</div></div></DuelShell>;
  }
  if (conn === "error" || conn === "closed") {
    return <DuelShell><div className="brutal bg-white p-10 text-center max-w-lg mx-auto mt-24"><div className="font-display font-black text-3xl">{lang === "es" ? "Tablero no disponible" : "Board unavailable"}</div></div></DuelShell>;
  }

  const Team = ({ t }) => (
    <div className="flex-1">
      <div className="brutal p-4 text-white text-center mb-3" style={{ background: TEAM_COLOR[t] }} data-testid={`board-team-${t}`}>
        <div className="text-sm font-display font-black uppercase tracking-widest truncate opacity-90">{state.team_names?.[t]}</div>
        <div className="font-display font-black text-6xl leading-none text-canales-yellow">{state.team_scores?.[t] ?? 0}</div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {(state.teams?.[t]?.players || []).map((p) => (
          <PlayerCard key={p.seller_id} player={p} isAnswerer={state.answerer === p.seller_id} maxStrikes={state.max_strikes} lang={lang} />
        ))}
      </div>
    </div>
  );

  const status = state.status;
  const winnerName = state.winner_team ? state.team_names?.[state.winner_team] : null;

  return (
    <DuelShell>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <CanalesLogo width={130} />
          <h1 className="font-display font-black text-3xl md:text-5xl text-canales-yellow" style={{ WebkitTextStroke: "2px black", textShadow: "4px 4px 0 #E3000F" }}>
            {lang === "es" ? "DUELO DE EQUIPOS" : "TEAM DUEL"}
          </h1>
          <div className="brutal-sm bg-canales-red text-white px-3 py-1 font-display font-black inline-flex items-center gap-2" data-testid="board-live">
            <span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" /> {lang === "es" ? "EN VIVO" : "LIVE"}
          </div>
        </div>

        {/* Question + timer */}
        {["playing", "steal", "sudden_death"].includes(status) && state.question && (
          <div className="mb-4">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="brutal-sm bg-white px-3 py-1 font-display font-black">
                {status === "sudden_death"
                  ? (lang === "es" ? "MUERTE SÚBITA" : "SUDDEN DEATH")
                  : `${lang === "es" ? "Pregunta" : "Q"} ${Math.min(state.q_index + 1, state.q_total)}/${state.q_total}`}
              </div>
              <motion.div animate={secs <= 5 ? { scale: [1, 1.12, 1] } : {}} transition={{ repeat: Infinity, duration: 0.6 }}
                className="brutal-sm px-4 py-1 text-white font-display font-black text-2xl inline-flex items-center gap-2"
                style={{ background: secs <= 5 ? "#E3000F" : "#1D4E89" }} data-testid="board-timer">
                <Clock className="w-5 h-5" /> 00:{String(secs).padStart(2, "0")}
              </motion.div>
              {status === "steal" && <div className="brutal-sm px-3 py-1 font-display font-black text-black" style={{ background: "#FFD100" }}>🎯 {lang === "es" ? "ROBO" : "STEAL"}</div>}
            </div>
            <div className="brutal p-5 text-center" style={{ background: "#FFF5E1" }}>
              <div className="font-display font-black text-2xl md:text-3xl leading-tight" data-testid="board-question">
                {lang === "en" && state.question.prompt_en ? state.question.prompt_en : state.question.prompt_es}
              </div>
            </div>
            <div className="mt-3 max-w-3xl mx-auto">
              <Options question={state.question} lang={lang} selectable={false} reveal={reveal} />
            </div>
          </div>
        )}

        {status === "lobby" && (
          <div className="brutal bg-white p-8 text-center mb-4" data-testid="board-lobby">
            <div className="font-display font-black text-3xl">{lang === "es" ? "Esperando el inicio…" : "Waiting to start…"}</div>
            <div className="mt-2 text-neutral-600 font-body">{lang === "es" ? "Sala" : "Room"} <b className="text-canales-red text-2xl">{code}</b></div>
          </div>
        )}
        {status === "paused" && (
          <div className="brutal bg-canales-yellow p-4 text-center font-display font-black text-2xl mb-4">⏸ {lang === "es" ? "EN PAUSA" : "PAUSED"}</div>
        )}

        {/* Teams */}
        <div className="flex flex-col md:flex-row gap-4 items-start">
          <Team t="A" />
          <div className="font-display font-black text-4xl text-white self-center px-2">VS</div>
          <Team t="B" />
        </div>

        <AnimatePresence>
          {status === "done" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" data-testid="board-done">
              <motion.div initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }} className="brutal bg-white p-10 text-center max-w-lg">
                <Trophy className="w-20 h-20 text-canales-red mx-auto" />
                <div className="font-display font-black text-4xl mt-2">{lang === "es" ? "¡GANADOR!" : "WINNER!"}</div>
                {winnerName
                  ? <div className="font-display font-black text-5xl mt-2" style={{ color: TEAM_COLOR[state.winner_team] }}>{winnerName}</div>
                  : <div className="font-display font-black text-4xl text-canales-red mt-2">{lang === "es" ? "¡EMPATE!" : "TIE!"}</div>}
                <div className="mt-3 font-display font-black text-xl">{state.team_names?.A}: {state.team_scores?.A} &nbsp;·&nbsp; {state.team_names?.B}: {state.team_scores?.B}</div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DuelShell>
  );
}
